/**
 * Which files can carry a sermon (docs/15-MUSIC-AND-AI.md, "The sermon"),
 * decided the same way on both sides: the API refuses what the worker
 * cannot read, and the web's upload offers only what it can (its `accept`
 * list, its hint, its refusals).
 *
 * - `pdf`: sent to the model as a file, read natively.
 * - `office`: a Word document or a PowerPoint deck (.docx, .doc, .pptx,
 *   .ppt), turned into Markdown in the worker (office-oxide) and sent as
 *   text. OpenDocument, RTF, Pages and Keynote are refused with what to do.
 * - `text`: a plain-text or Markdown manuscript, read as it is.
 * - `audio`: a recording, transcribed first.
 */

export type SermonFileKind = 'pdf' | 'office' | 'text' | 'audio' | 'unsupported'

/** The Office formats the worker reads (office-oxide's names for them). */
export type OfficeFormat = 'docx' | 'doc' | 'pptx' | 'ppt'

/**
 * The largest sermon file each kind may be. A document — a PDF, a Word or
 * PowerPoint file — is 20MB (a manuscript with images, a deck with photos:
 * ample); a recording is what one transcription request carries.
 */
export const SERMON_FILE_LIMITS: Record<Exclude<SermonFileKind, 'unsupported'>, number> = {
  pdf: 20 * 1024 * 1024,
  office: 20 * 1024 * 1024,
  /** A plain-text or Markdown manuscript. */
  text: 1024 * 1024,
  /** One transcription request: about 50 minutes of speech at 64 kbps. */
  audio: 25 * 1024 * 1024,
}

/** What an upload of a sermon offers to choose: its `accept` list. */
export const SERMON_FILE_ACCEPT = [
  '.pdf',
  '.docx',
  '.doc',
  '.pptx',
  '.ppt',
  '.txt',
  '.md',
  '.mp3',
  '.m4a',
  '.wav',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-powerpoint',
  'text/plain',
  'text/markdown',
  'audio/*',
].join(',')

/** The upload's hint: what a sermon can be, and how large. */
export const SERMON_FILE_HINT =
  'PDF, Word or PowerPoint up to 20 MB, text or Markdown; a recording as MP3, M4A or WAV, up to 25 MB'

const OFFICE_TYPES: Record<string, OfficeFormat> = {
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'application/vnd.ms-powerpoint': 'ppt',
}

const OFFICE_EXTENSIONS: readonly string[] = ['docx', 'doc', 'pptx', 'ppt']

const AUDIO_FORMATS: Record<string, string> = {
  'audio/mpeg': 'mp3',
  'audio/mp3': 'mp3',
  'audio/mp4': 'm4a',
  'audio/x-m4a': 'm4a',
  'audio/m4a': 'm4a',
  'audio/aac': 'aac',
  'audio/wav': 'wav',
  'audio/x-wav': 'wav',
  'audio/wave': 'wav',
}

const extension = (fileName: string | null) =>
  /\.([a-z0-9]+)$/i.exec(fileName ?? '')?.[1]?.toLowerCase() ?? ''

const mediaType = (contentType: string | null) =>
  (contentType ?? '').split(';')[0]?.trim().toLowerCase() ?? ''

/**
 * The Office format of a file, by its name first (a browser may call a
 * .docx `application/octet-stream` or `application/zip`), then its type.
 */
export function officeFormat(
  contentType: string | null,
  fileName: string | null,
): OfficeFormat | null {
  const ext = extension(fileName)
  if (OFFICE_EXTENSIONS.includes(ext)) return ext as OfficeFormat
  return OFFICE_TYPES[mediaType(contentType)] ?? null
}

const TYPES_BY_EXTENSION: Record<string, string> = {
  md: 'text/markdown',
  markdown: 'text/markdown',
  txt: 'text/plain',
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  doc: 'application/msword',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ppt: 'application/vnd.ms-powerpoint',
  mp3: 'audio/mpeg',
  m4a: 'audio/x-m4a',
  wav: 'audio/wav',
}

/**
 * The type to upload a sermon file as: the browser's, else what its name
 * says (browsers leave a .md's type empty, and some an old .doc's).
 */
export function sermonUploadType(type: string, fileName: string): string {
  return type || TYPES_BY_EXTENSION[extension(fileName)] || 'application/octet-stream'
}

/** The kind of a sermon file, by its type and its name (which browsers fill in for .md). */
export function sermonFileKind(
  contentType: string | null,
  fileName: string | null,
): SermonFileKind {
  const type = mediaType(contentType)
  const ext = extension(fileName)
  if (type === 'application/pdf' || ext === 'pdf') return 'pdf'
  if (officeFormat(contentType, fileName)) return 'office'
  if (
    type === 'text/plain' ||
    type === 'text/markdown' ||
    ext === 'txt' ||
    ext === 'md' ||
    ext === 'markdown'
  )
    return 'text'
  if (type in AUDIO_FORMATS || ['mp3', 'm4a', 'wav', 'aac'].includes(ext)) return 'audio'
  return 'unsupported'
}

/** The format a transcription request names for an audio file ("mp3", "m4a", "wav"). */
export function audioFormat(contentType: string | null, fileName: string | null): string {
  return AUDIO_FORMATS[mediaType(contentType)] ?? (extension(fileName) || 'mp3')
}

/** What a sermon document is, as the model is told: "a Word document". */
export function sermonDocumentLabel(contentType: string | null, fileName: string | null): string {
  const kind = sermonFileKind(contentType, fileName)
  if (kind === 'pdf') return 'a PDF'
  if (kind === 'audio') return 'a recording'
  if (kind === 'text') return extension(fileName) === 'txt' ? 'a text file' : 'a Markdown file'
  const format = officeFormat(contentType, fileName)
  return format === 'pptx' || format === 'ppt' ? 'a PowerPoint deck' : 'a Word document'
}

const SAVE_AS = 'save it as a Word document (.docx) or a PDF and use that'

/** Why a file cannot be the sermon, in words; null when it can. */
export function sermonFileProblem(file: {
  contentType: string | null
  fileName: string | null
  sizeBytes: number | null
}): string | null {
  const kind = sermonFileKind(file.contentType, file.fileName)
  if (kind === 'unsupported') {
    const ext = extension(file.fileName)
    if (['odt', 'odp', 'ott'].includes(ext)) return `OpenDocument files can’t be read: ${SAVE_AS}.`
    if (ext === 'rtf' || mediaType(file.contentType) === 'application/rtf')
      return `Rich Text files can’t be read: ${SAVE_AS}.`
    if (ext === 'pages' || ext === 'key')
      return 'Pages and Keynote files can’t be read: export it as a Word document, a PowerPoint deck or a PDF.'
    return 'A sermon can be a PDF, a Word document or PowerPoint deck, a text or Markdown file, or a recording (MP3, M4A, WAV).'
  }
  const limit = SERMON_FILE_LIMITS[kind]
  if ((file.sizeBytes ?? 0) > limit)
    return `That ${kind === 'audio' ? 'recording' : 'file'} is over ${Math.round(limit / (1024 * 1024))}MB, the most a sermon can be.`
  return null
}

/**
 * An item that looks like the sermon, by its title ("Sermon", "Message",
 * "Preaching", "The Word"): its files come first when choosing the sermon's.
 */
export function looksLikeSermonItem(title: string | null): boolean {
  return /\b(sermons?|messages?|preaching|preach|word)\b/i.test(title ?? '')
}
